import { digger } from '@cbre/digger';
import { concatValuesBy, isDash } from '@miq/utils';
import moment from 'moment';
import { map } from 'rxjs/operators';
import { ReportRecord } from '../../../../models/report.model';
import { LovValues, TemplateFieldsDisplayFn } from '../../../../models/template.model';
import { usageAmenitiesData, getMediaInformation, amenitiesData } from '../../templates-formatter.const';
import { displayFnWrapper, mapAndFormatDynamicValue } from '../../utils/dynamic-data-retrievers';
import { AvailabilityTypesLabels, USAGE_AMENITY_FIELD } from '../common';
import { currencyLov, PropertyType } from '../us/us.common';
import { getFooterLabel, amenitiesLov } from './pacific-availability.common';

export const displayFnMap: TemplateFieldsDisplayFn = {
  262: {
    displayFn: (record: ReportRecord, options, amenities) => {
      return amenitiesLov(options).pipe(
        map((amenitiesLOV: LovValues[]) => {
          const result = usageAmenitiesData(record, amenities, PropertyType.Retail, 6, USAGE_AMENITY_FIELD);
          const lovData = amenitiesData(result, amenitiesLOV, options);
          return lovData;
        })
      );
    },
  }, // Amenities
  222: {
    displayFn: (record: ReportRecord, _, city, suburb) => {
      const suburbVal = mapAndFormatDynamicValue(record, suburb, null, null, false, '') as string;
      const cityVal = mapAndFormatDynamicValue(record, city, null, null, false, '') as string;
      if (cityVal && suburbVal) {
        return concatValuesBy(' / ', cityVal, suburbVal);
      } else if (cityVal) {
        return cityVal;
      } else if (suburbVal) {
        return suburbVal;
      } else {
        return ' ';
      }
    },
  }, // Subheader
  224: { displayFn: displayFnWrapper }, // Property Information
  236: { displayFn: displayFnWrapper }, // Size
  237: {
    displayFn: (record: ReportRecord, options, askingPrice) => {
      return currencyLov(options).pipe(
        map((currencyLOV: LovValues[]) => {
          const nzcurrency = currencyLOV.map(currency => {
            if (currency.key === 'New Zealand Dollar') {
              currency.value = '$';
            }
            return currency;
          });
          const uomArgument = { ...askingPrice.uomArgument, isPrefix: true };
          askingPrice = { ...askingPrice, uomArgument };
          return mapAndFormatDynamicValue(record, askingPrice, nzcurrency, true, false);
        })
      );
    },
  }, // Asking Rental
  238: {
    displayFn: (record: ReportRecord, options, outgoings) => {
      return currencyLov(options).pipe(
        map((currencyLOV: LovValues[]) => {
          const nzcurrency = currencyLOV.map(currency => {
            if (currency.key === 'New Zealand Dollar') {
              currency.value = '$';
            }
            return currency;
          });
          const uomArgument = { ...outgoings.uomArgument, isPrefix: true };
          outgoings = { ...outgoings, uomArgument };
          return mapAndFormatDynamicValue(record, outgoings, nzcurrency, true, false);
        })
      );
    },
  }, // Outgoings
  239: {
    displayFn: (record: ReportRecord, _, leaseTerm) => {
      const leaseValue = mapAndFormatDynamicValue(record, leaseTerm);
      const result = leaseValue == null || isDash(leaseValue as string) ? '-' : `${leaseValue} Years`;
      return result;
    },
  }, // Lease Term
  240: {
    displayFn: (record: ReportRecord, _, availableDate) => {
      const avalDate = mapAndFormatDynamicValue(record, availableDate);
      return !isDash(avalDate as string) ? moment(avalDate).format('DD/MM/YYYY').toString() : avalDate;
    },
  }, // Availability
  241: { displayFn: displayFnWrapper }, // Carparking
  242: { displayFn: displayFnWrapper }, // Location Information
  49: { displayFn: displayFnWrapper }, // Property Name
  552: {
    displayFn: (record: ReportRecord, _, _propertyName, streetName) => {
      return mapAndFormatDynamicValue(record, streetName);
    },
    routeFn: ({ associatedProperty }) => {
      return ['/records', 'property', digger(associatedProperty, 'entity.id'), 'details'];
    },
    isVisible: (record: ReportRecord, settings) => {
      const propertyName = settings.field.displayFnArguments[0];
      const streetName = settings.field.displayFnArguments[1];
      let propertyNameVal = mapAndFormatDynamicValue(record, propertyName);
      let addressVal = mapAndFormatDynamicValue(record, streetName);
      if (propertyNameVal && addressVal) {
        const properyArray: Array<string> = (<string>propertyNameVal).split(' ').filter(Boolean);
        const addressArray: Array<string> = (<string>addressVal).split(' ').filter(Boolean);
        if (properyArray.length !== addressArray.length) {
          return true;
        }
        properyArray.pop();
        addressArray.pop();
        propertyNameVal = properyArray.join(' ');
        addressVal = addressArray.join(' ');
        return propertyNameVal !== addressVal;
      }
    },
  },
  // Property Address
  50: { displayFn: displayFnWrapper }, // Location
  223: { displayFn: displayFnWrapper }, // Contacts
  98: {
    displayFn: (record: ReportRecord) => {
      return getMediaInformation(record);
    },
  }, // Primary Image
  99: {
    displayFn: (record: ReportRecord) => {
      return getMediaInformation(record);
    },
  }, // Additional Image
  254: {
    displayFn: (record: ReportRecord) => {
      return getMediaInformation(record);
    },
  }, // Secondary Image
  259: {
    displayFn: (record: ReportRecord, _, availableType) => {
      const availableTypeVal = mapAndFormatDynamicValue(record, availableType) as string;
      const result = !!AvailabilityTypesLabels[availableTypeVal] ? AvailabilityTypesLabels[availableTypeVal] : '-';
      return result;
    },
  }, // title based on availability Type
  347: {
    displayFn: (record: ReportRecord) => {
      return getMediaInformation(record);
    },
  },
  553: {
    displayFn: (record: ReportRecord, _, suite, floorNumber) => {
      const suiteVal = mapAndFormatDynamicValue(record, suite, null, null, false);
      const floorNumberVal = mapAndFormatDynamicValue(record, floorNumber, null, null, false);
      const floorPlanConst = ['Floorplan: ', 'Floor: ', ', Suite: '];
      const suitValArr: string[] = !Array.isArray(suiteVal) ? [suiteVal] : suiteVal;

      return (
        floorPlanConst[0] +
        suitValArr.map((val, i) => floorPlanConst[1] + concatValuesBy(floorPlanConst[2], floorNumberVal[i], val))
      );
    },
  }, // Floor/Suite
  ...getFooterLabel(),
};
